import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { AdminPage, Card, Gate, H, Meta } from "@/components/admin-ui";
import { Button, ErrorState, Field, Loading } from "@/components/ui";

// Legal pages are edited as plain text: "## Heading" lines start a section.
const toText = (l) => l.sections.map((s) => `## ${s.h}\n${s.p}`).join("\n\n");
const fromText = (t) => t.split(/^## /m).map((b) => b.trim()).filter(Boolean).map((b) => {
  const [h, ...rest] = b.split("\n");
  return { h: h.trim(), p: rest.join("\n").trim() };
}).filter((s) => s.h && s.p);

export default function AdminContent() {
  return <Gate perm="content" testId="admin-content-forbidden"><Inner /></Gate>;
}

function Inner() {
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-content"], queryFn: () => api("/admin/content") });
  const [tpl, setTpl] = useState(null);
  const [legal, setLegal] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async (path, body, done) => {
    setBusy(true);
    try { await api(path, { method: "PUT", body }); toast("Saved", "success"); done(); refetch(); } catch (e) { toast(e.message, "error"); } finally { setBusy(false); }
  };
  if (isLoading) return <Loading />;
  if (isError || !data) return <ErrorState testId="admin-content-error" message={error?.message ?? ""} onRetry={() => refetch()} />;
  return (
    <AdminPage title="Content" testId="admin-content-screen">
      <H>Email templates</H>
      <Meta>Only the listed placeholders are allowed. WhatsApp numbers and verification documents can never be inserted.</Meta>
      <div className="grid gap-3 md:grid-cols-2">
        {data.email_templates.map((t) => (
          <Card key={t.key} testId={`content-template-${t.key}`} onClick={() => setTpl(t)}><p className="font-bold">{t.name}</p><Meta>{t.subject}</Meta></Card>
        ))}
      </div>
      <H>Legal & safety pages</H>
      <div className="grid gap-3 md:grid-cols-2">
        {data.legal.map((l) => (
          <Card key={l.key} testId={`content-legal-${l.key}`} onClick={() => setLegal({ key: l.key, title: l.title, text: toText(l) })}>
            <p className="font-bold">{l.title}</p><Meta>{l.sections.length} sections · updated {l.updated}</Meta>
          </Card>
        ))}
      </div>
      <Modal open={!!tpl} onClose={() => setTpl(null)} title={tpl?.name ?? ""} testId="content-template-sheet" wide>
        {tpl ? (
          <>
            <p className="mb-4 font-mono text-xs text-muted-foreground" data-testid="content-template-placeholders">
              Placeholders: {tpl.placeholders.map((p) => `{{${p}}}`).join("  ")}{tpl.required.length ? ` · required: ${tpl.required.map((p) => `{{${p}}}`).join(", ")}` : ""}
            </p>
            <Field testId="content-template-subject" label="Subject" value={tpl.subject} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} />
            <Field testId="content-template-body" label="Body (blank line = new paragraph)" multiline rows={8} value={tpl.body} onChange={(e) => setTpl({ ...tpl, body: e.target.value })} />
            <Button testId="content-template-save" title="Save template" loading={busy} onClick={() => save(`/admin/content/email/${tpl.key}`, { subject: tpl.subject, body: tpl.body }, () => setTpl(null))} />
          </>
        ) : null}
      </Modal>
      <Modal open={!!legal} onClose={() => setLegal(null)} title="Edit page" testId="content-legal-sheet" wide>
        {legal ? (
          <>
            <Field testId="content-legal-title" label="Title" value={legal.title} onChange={(e) => setLegal({ ...legal, title: e.target.value })} />
            <Field testId="content-legal-body" label='Sections — start each with "## Heading"' multiline rows={14} value={legal.text} onChange={(e) => setLegal({ ...legal, text: e.target.value })} />
            <Meta testId="content-legal-sections-count">{fromText(legal.text).length} sections detected</Meta>
            <Button testId="content-legal-save" title="Publish page" loading={busy} className="mt-4"
              onClick={() => save(`/admin/content/legal/${legal.key}`, { title: legal.title, sections: fromText(legal.text) }, () => setLegal(null))} />
          </>
        ) : null}
      </Modal>
    </AdminPage>
  );
}
